import React, { FC, useCallback, useState } from 'react';
import { Modal } from 'components/Modal';
import PhoneForm from 'components/PhoneForm';
import { Title } from 'components/Title';
import styles from './PushInvite.module.scss';
import { InputText } from 'ui/InputText';
import { Button } from 'ui/Button';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';
import { Loader } from '@mantine/core';
import {
  getAuthorizationHeader,
  useInviteEmployedsMutation,
  useInviteMultipleEmployedsMutation,
} from 'redux/Api/employedsApi/employedsApi';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';
import type {
  InviteEmployedsResponseType,
  InviteMultipleEmployedsResponseType,
} from 'redux/Api/employedsApi/type';
import classNames from 'classnames';
import { CopyToClipboardButton } from 'ui/CopyToClipboardButton';
import { ReactComponent as CheckIcon } from 'assets/img/filter/check.svg';
import { TabMenuFilter } from 'components/TabMenuFilter';
import { ReactComponent as UploadIcon } from './img/iconUpload.svg';
import { ReactComponent as FolderIcon } from './img/folder.svg';
import { ReactComponent as DownloadIcon } from './img/iconDownload.svg';
import { ReactComponent as CheckStatusIcon } from './img/iconCheck.svg';
import { ReactComponent as InfoStatusIcon } from './img/iconInfo.svg';
import { useDropzone } from 'react-dropzone';
import { ReactComponent as CloseSvg } from 'assets/img/close.svg';
import { getFileNameFromContentDisposition } from 'utils/strActions';
import { ReactComponent as IconDownload } from './img/iconDownload2.svg';
import { send } from 'process';
import useYandexMetrica from 'hooks/useYandexMetrica';

interface PushInviteTypes {
  setShowPushInvite: (e: boolean) => void;
  showPushInvite: boolean;
  refetch: () => void;
}

export const PushInvite: FC<PushInviteTypes> = ({
  setShowPushInvite,
  showPushInvite,
  refetch,
}) => {
  const templateUrl = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_EMPLOYEES_TEMPLATE_PATH}`;
  const { sendGoal } = useYandexMetrica();

  const [valuePhone, setValuePhone] = useState('');
  const [fio, setFio] = useState('');
  const [fioError, setFioError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [report, setReport] = useState<InviteMultipleEmployedsResponseType | null>(null);
  const [reportError, setReportError] = useState<string>('');
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);

  const disabled = valuePhone.length < 10 || fio.length < 1;

  const [invite, { data, isLoading: isLoadingOne }] = useInviteEmployedsMutation();
  const [inviteMultiple, { data: dataMultiple, isLoading }] =
    useInviteMultipleEmployedsMutation();

  const [file, setFile] = useState<File | null>(null);
  const inviteHandler = async () => {
    await invite({
      body: {
        name: fio,
        phone: `7${valuePhone}`,
      },
    }).then(
      (data: {
        data?: InviteEmployedsResponseType;
        error?: FetchBaseQueryError | SerializedError;
      }) => {
        if (data?.data) {
          setTimeout(() => {
            setValuePhone('');
            setFio('');
            setFioError('');
            setPhoneError('');
          }, 2000);
          refetch();
        }
        if (data.error && 'data' in data.error) {
          const errorData = data.error.data as {
            errors?: { phone?: string[]; name?: string[] };
          };
          const { phone, name } = errorData.errors || {};

          if (phone?.[0]) {
            setPhoneError(phone?.[0]);
          } else {
            setPhoneError('');
          }

          if (name?.[0]) {
            setFioError(name?.[0]);
          } else {
            setFioError('');
          }
        }
      },
    );
  };

  const inviteMultipleHandler = async () => {
    if (file) {
      const formData = new FormData();
      formData.append('workers', file);

      await inviteMultiple(formData)
        .then(
          (data: {
            data?: InviteMultipleEmployedsResponseType;
            error?: FetchBaseQueryError | SerializedError;
          }) => {
            if (data?.data) {
              setReport(data.data);
            } else if (data?.error && 'data' in data.error) {
              setReport(null);
              setFile(null);
              const errorData = data.error.data as { errors?: { workers?: string[] } };
              setReportError(errorData.errors?.workers?.[0] || '');
            }
          },
        )
        .catch((e) => console.log('e', e));
    }
  };

  const handleDownloadReport = async () => {
    if (report?.report_file) {
      const response = await fetch(report?.report_file, {
        method: 'GET',
        headers: { Authorization: getAuthorizationHeader() },
      });

      const type = response.headers.get('content-type') || undefined;
      const disposition = response.headers.get('content-disposition');
      const arrayBuffer = await response.blob();

      const blob = new Blob([arrayBuffer], {
        type,
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getFileNameFromContentDisposition(disposition));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const keyHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !disabled) {
      inviteHandler();
    }
  };

  const [activeTab, setActiveTab] = useState('1');

  const tabList = [
    { key: '1', name: 'Одного' },
    { key: '2', name: 'Реестр' },
  ];

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) setFile(acceptedFiles[0]);
    setReportError('');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
    },
  });

  const handleCopy = () => {
    setShowCopiedSuccess(true);
  };

  const handleCloseCopiedSuccess = () => {
    setShowCopiedSuccess(false);
  };

  return data || dataMultiple ? (
    <SuccessErrorInfo
      type='success'
      handleClose={() => setShowPushInvite(false)}
      text={dataMultiple ? 'Исполнители приглашены' : 'Исполнитель приглашен'}
    />
  ) : (
    <>
      <Modal setOpen={setShowPushInvite} open={showPushInvite}>
        <div className={styles.root}>
          <div>
            <Title title='Приглашение исполнителей' />
            {/* <CloseIcon /> */}
          </div>
          <TabMenuFilter
            active={activeTab}
            setActive={(tab: string) => {
              setActiveTab(tab);

              if (tab !== activeTab) {
                setReportError('');
              }
            }}
            lists={tabList}
            fullWidth
          />
          {activeTab === '1' ? (
            <>
              <span className={styles.subTitle}>
                Отправь исполнителю: Скачай бесплатное приложение{' '}
                <a href='https://skilla.ru/d' className={styles.link}>
                  skilla.ru/d
                </a>
                »
                <CopyToClipboardButton
                  content={'Скачай бесплатное приложение https://skilla.ru/d'}
                  onCopy={handleCopy}
                />
              </span>

              <div className={styles.root_fio}>
                <span className={styles.placeholder}>Имя</span>
                <InputText
                  error={!!fioError}
                  nameError='series'
                  placeholderValue='Иванов Алексей Михайлович'
                  type='text'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFio(e.target.value);
                    setFioError('');
                  }}
                  style={{ height: '48px', border: 'none' }}
                  value={fio}
                />
                {fioError && <span className={styles.placeholderError}>{fioError}</span>}
              </div>
              <div className={styles.root_phone}>
                <span className={styles.placeholder}>Номер телефона</span>
                <PhoneForm
                  value={valuePhone}
                  onChange={(v) => {
                    setValuePhone(v);
                    setPhoneError('');
                  }}
                  error={phoneError}
                  inputClassName={classNames(
                    styles.input,
                    phoneError ? styles.input_error : '',
                  )}
                  placeholder='+7 (000) 000-00-00'
                  inputStyle={{ height: '48px' }}
                  onKeyDown={(e) => keyHandler(e)}
                />
                {phoneError && (
                  <span className={styles.placeholderError}>{phoneError}</span>
                )}
              </div>
              <Button
                onClick={() => {
                  inviteHandler();
                  sendGoal('InviteExecutor');
                }}
                className={styles.btn}
                theme='filed'>
                <div style={{ display: 'flex', alignContent: 'flex-end' }}>
                  {isLoadingOne ? (
                    <div
                      style={{
                        display: 'flex',
                        alignContent: 'flex-end',
                        gap: '10px',
                      }}>
                      {'Приглашаем'} <Loader size='xs' color='white' />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignContent: 'flex-end' }}>
                      Пригласить <CheckIcon />
                    </div>
                  )}
                </div>
              </Button>
            </>
          ) : report ? (
            <>
              <div className={styles.reportStatus}>
                {report.import_count === report.upload_count ? (
                  <CheckStatusIcon />
                ) : (
                  <InfoStatusIcon />
                )}
                <p>
                  Загружено {report.import_count} из {report.upload_count} исполнителей
                </p>
              </div>
              <div
                className={styles.file}
                onClick={handleDownloadReport}
                style={{ cursor: 'pointer' }}>
                <div>
                  <FolderIcon />
                  <p>Отчет.xlsx</p>
                </div>
                <DownloadIcon className={styles.file_icon} />
              </div>
            </>
          ) : (
            <div className={styles.wrapper}>
              <span className={styles.subTitle}>
                Пригласи несколько исполнителей сразу. <br />
                Отправь исполнителям: Скачай бесплатное приложение{' '}
                <a href='https://skilla.ru/d' className={styles.link}>
                  skilla.ru/d
                </a>
                »
                <CopyToClipboardButton
                  content={'Скачай бесплатное приложение https://skilla.ru/d'}
                  onCopy={handleCopy}
                />
              </span>
              <a
                className={classNames(styles.link, styles.template)}
                style={!file ? { marginBottom: '4px' } : {}}
                href={templateUrl}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Шаблон реестра исполнителей <IconDownload />
                </div>
              </a>
              {file ? (
                <>
                  <div className={styles.file} style={{ margin: '4px 0' }}>
                    <div>
                      <FolderIcon />
                      <p>{file.name}</p>
                    </div>
                    <CloseSvg
                      onClick={() => setFile(null)}
                      className={styles.file_icon}
                    />
                  </div>
                  <Button
                    onClick={() => {
                      inviteMultipleHandler();
                      sendGoal('UploadExecutorRegistry');
                    }}
                    className={styles.btn}
                    disabled={isLoading}
                    theme='filed'>
                    {isLoading ? (
                      <div
                        style={{
                          display: 'flex',
                          alignContent: 'flex-end',
                          gap: '10px',
                        }}>
                        {'Приглашаем'} <Loader size='xs' color='white' />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignContent: 'flex-end' }}>
                        Пригласить <CheckIcon />
                      </div>
                    )}
                  </Button>
                </>
              ) : (
                <div>
                  <div
                    className={classNames(
                      styles.upload,
                      isDragActive && styles.upload_active,
                    )}
                    {...getRootProps()}>
                    <input {...getInputProps()} />
                    <p className={styles.upload_title}>
                      Перетащите или <span className={styles.link}>загрузите файл</span>{' '}
                      <UploadIcon />
                    </p>
                    <p className={styles.upload_subtitle}>Файлы .XLSX, до 500 Кбайт</p>
                  </div>
                  {reportError && <p className={styles.error}>{reportError}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
      {showCopiedSuccess && (
        <SuccessErrorInfo
          type='success'
          text='Скопировано в буфер обмена'
          handleClose={handleCloseCopiedSuccess}
        />
      )}
    </>
  );
};
